import React, { useState, useEffect } from 'react'
import { security, USER_ROLES } from '../config/security'
import './KitSolar.css'

const KitSolar = ({ onBack, userRole }) => {
  // Estados para os dados
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [meta, setMeta] = useState({ page: 1, size: 10, count: 0 })
  
  // Estados para os filtros
  const [filters, setFilters] = useState({
    category: 'MODULES', // Padrão: módulos
    description: '',
    region: 'PA',
    channel: 'autoservico',
    page: 1,
    size: 10
  })
  
  // Estados para autenticação automática
  const [token, setToken] = useState('')
  const [tokenExpiry, setTokenExpiry] = useState(null)

  // Carregar token salvo quando o componente montar
  useEffect(() => {
    loadSavedToken()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar produtos quando o componente montar ou filtros mudarem
  useEffect(() => {
    fetchProducts()
  }, [filters, token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar permissão de acesso
  if (!security.canAccessKitSolar(userRole)) {
    return (
      <div className="kit-solar-container">
        <div className="kit-solar-header">
          <h1 className="kit-solar-title">🔋 Kit Solar</h1>
          <button onClick={onBack} className="back-button">
            ← Voltar ao Dashboard
          </button>
        </div>
        <div className="access-denied">
          <h2>🚫 Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta funcionalidade.</p>
          <p>Apenas administradores podem acessar o Kit Solar.</p>
        </div>
      </div>
    )
  }
  
  // Credenciais fixas
  const credentials = {
    username: 'tecsol.ananindeua@gmail.com',
    password: 'JUwa@2024' // Substitua pela senha real
  }

  // Função para obter token de acesso
  const getAccessToken = async () => {
    try {
      const formData = new URLSearchParams()
      formData.append('grant_type', 'password')
      formData.append('client_id', 'ecommerce')
      formData.append('username', credentials.username)
      formData.append('password', credentials.password)

      const response = await fetch('https://sso.solfacil.com.br/realms/General/protocol/openid-connect/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Erro na autenticação: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.error) {
        throw new Error(data.error_description || data.error)
      }

      // Calcular quando o token expira
      const expiresAt = new Date(Date.now() + (data.expires_in * 1000))
      
      // Salvar token e informações no localStorage
      localStorage.setItem('solfacil_token', data.access_token)
      localStorage.setItem('solfacil_token_expiry', expiresAt.toISOString())
      localStorage.setItem('solfacil_credentials', JSON.stringify(credentials))

      setToken(data.access_token)
      setTokenExpiry(expiresAt)
      
      console.log('Token obtido com sucesso. Expira em:', expiresAt.toLocaleString('pt-BR'))
      
      return data.access_token
    } catch (error) {
      console.error('Erro ao obter token:', error)
      throw error
    }
  }

  // Função para verificar se o token está válido
  const isTokenValid = () => {
    if (!token || !tokenExpiry) return false
    const now = new Date()
    // Renovar token 5 minutos antes de expirar
    const renewTime = new Date(tokenExpiry.getTime() - (5 * 60 * 1000))
    return now < renewTime
  }

  // Função para carregar token salvo
  const loadSavedToken = () => {
    try {
      const savedToken = localStorage.getItem('solfacil_token')
      const savedExpiry = localStorage.getItem('solfacil_token_expiry')
      const savedCredentials = localStorage.getItem('solfacil_credentials')

      if (savedToken && savedExpiry && savedCredentials) {
        const expiry = new Date(savedExpiry)
        const now = new Date()
        
        // Se ainda não expirou (com margem de 5 minutos)
        if (now < new Date(expiry.getTime() - (5 * 60 * 1000))) {
          setToken(savedToken)
          setTokenExpiry(expiry)
          console.log('Token carregado do cache. Expira em:', expiry.toLocaleString('pt-BR'))
          return true
        } else {
          console.log('Token expirado, será renovado')
          // Limpar token expirado
          localStorage.removeItem('solfacil_token')
          localStorage.removeItem('solfacil_token_expiry')
        }
      }
    } catch (error) {
      console.error('Erro ao carregar token salvo:', error)
    }
    return false
  }

  // Função para fazer a requisição GraphQL
  const fetchProducts = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Verificar se precisa obter/renovar token
      let currentToken = token
      if (!isTokenValid()) {
        currentToken = await getAccessToken()
      }
      const query = `
        query getSpareProducts($description: String!, $page: Int!, $size: Int!, $category: String!, $region: String!, $channel: String!) {
          getSpareProducts(
            description: $description
            page: $page
            size: $size
            category: $category
            region: $region
            channel: $channel
          ) {
            meta {
              page
              size
              count
              __typename
            }
            facets {
              filter_name
              options {
                name
                value
                selected
                __typename
              }
              __typename
            }
            products {
              sku
              region
              dc_id
              image
              manufacturer
              model
              description
              price
              total_price
              in_cart
              quantity
              info {
                title
                value
                __typename
              }
              datasheet
              __typename
            }
            cart {
              id
              should_confirm_on_add
              subtotal
              tag
              enabled
              __typename
            }
            __typename
          }
        }
      `

      const variables = {
        description: filters.description,
        category: filters.category,
        channel: filters.channel,
        page: filters.page,
        region: filters.region,
        size: filters.size
      }


      // Requisição para a API real
      const response = await fetch('https://kong.solfacil.com.br/prd-bff-store/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
          // Adicione outros headers de autenticação se necessário
        },
        body: JSON.stringify({
          operationName: 'getSpareProducts',
          query,
          variables
        })
      })

      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.errors) {
        throw new Error(data.errors[0].message)
      }

      // Debug: verificar estrutura dos dados recebidos
      console.log('Resposta da API:', data)
      console.log('Produtos recebidos:', data.data.getSpareProducts.products)
      
      setProducts(data.data.getSpareProducts.products || [])
      setMeta(data.data.getSpareProducts.meta || { page: 1, size: 10, count: 0 })
      
    } catch (err) {
      console.error('Erro ao buscar produtos:', err)
      setError(err.message)
      // Dados de exemplo para demonstração
      setProducts([
        {
          sku: 'MOD001',
          manufacturer: 'Canadian Solar',
          model: 'CS3K-395MS',
          description: 'Módulo fotovoltaico de 395W',
          price: 850.00,
          total_price: 850.00,
          image: 'https://via.placeholder.com/300x200?text=Módulo+Solar',
          info: [
            { title: 'Potência', value: '395W' },
            { title: 'Eficiência', value: '20.1%' },
            { title: 'Garantia', value: '25 anos' }
          ]
        },
        {
          sku: 'INV001',
          manufacturer: 'Fronius',
          model: 'Primo 5.0-1',
          description: 'Inversor string de 5kW',
          price: 3200.00,
          total_price: 3200.00,
          image: 'https://via.placeholder.com/300x200?text=Inversor',
          info: [
            { title: 'Potência', value: '5kW' },
            { title: 'Eficiência', value: '97.5%' },
            { title: 'Garantia', value: '10 anos' }
          ]
        }
      ])
      setMeta({ page: 1, size: 10, count: 2 })
    } finally {
      setLoading(false)
    }
  }

  // Handlers para mudanças nos filtros
  const handleCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, category, page: 1 }))
  }

  const handleDescriptionChange = (description) => {
    setFilters(prev => ({ ...prev, description, page: 1 }))
  }

  const handleRegionChange = (region) => {
    setFilters(prev => ({ ...prev, region, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  // Renderizar produto individual
  const renderProduct = (product) => {
    // Debug: verificar dados do produto
    console.log('Produto individual:', product)
    console.log('Preço do produto:', { price: product.price, total_price: product.total_price })
    
    return (
    <div key={product.sku} className="product-card">
      <img 
        src={product.image || 'https://via.placeholder.com/300x200?text=Produto'} 
        alt={product.model}
        className="product-image"
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/300x200?text=Sem+Imagem'
        }}
      />
      
      <div className="product-manufacturer">{product.manufacturer}</div>
      <div className="product-model">{product.model}</div>
      <div className="product-description">{product.description}</div>
      
      <div className="product-price">
        R$ {(product.price || product.total_price)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || 'N/A'}
      </div>
      
      <div className="product-sku">SKU: {product.sku}</div>
      
      {product.info && product.info.length > 0 && (
        <div className="product-info">
          {product.info.map((info, index) => (
            <div key={index} className="product-info-item">
              <span className="product-info-label">{info.title}:</span>
              <span className="product-info-value">{info.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
    )
  }

  // Renderizar paginação
  const renderPagination = () => {
    const totalPages = Math.ceil(meta.count / meta.size)
    if (totalPages <= 1) return null

    return (
      <div className="pagination-container">
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(filters.page - 1)}
          disabled={filters.page <= 1}
        >
          ← Anterior
        </button>
        
        <span className="pagination-info">
          Página {filters.page} de {totalPages}
        </span>
        
        <button 
          className="pagination-button"
          onClick={() => handlePageChange(filters.page + 1)}
          disabled={filters.page >= totalPages}
        >
          Próxima →
        </button>
      </div>
    )
  }

  return (
    <div className="kit-solar-container">
      <div className="kit-solar-header">
        <h1 className="kit-solar-title">🔋 Kit Solar</h1>
        <button onClick={onBack} className="back-button">
          ← Voltar ao Dashboard
        </button>
      </div>


      {/* Filtros */}
      <div className="filters-container">
        <h3 className="filters-title">Filtros</h3>
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">Categoria</label>
            <select 
              className="filter-select"
              value={filters.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="MODULES">Módulos Fotovoltaicos</option>
              <option value="INVERTERS">Inversores</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Região</label>
            <select 
              className="filter-select"
              value={filters.region}
              onChange={(e) => handleRegionChange(e.target.value)}
            >
              <option value="PA">Pará</option>
              <option value="SP">São Paulo</option>
              <option value="RJ">Rio de Janeiro</option>
              <option value="MG">Minas Gerais</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Buscar</label>
            <input 
              type="text"
              className="search-input"
              placeholder="Digite o modelo ou fabricante..."
              value={filters.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Container de produtos */}
      <div className="products-container">
        <div className="products-header">
          <div className="products-count">
            {loading ? 'Carregando...' : `${meta.count} produtos encontrados`}
          </div>
        </div>

        {error && (
          <div className="error-message">
            ⚠️ Erro ao carregar produtos: {error}
            <br />
            {error.includes('autenticação') ? (
              <small>Erro na autenticação. Clique em "Conectar à API" para tentar novamente.</small>
            ) : (
              <small>Exibindo dados de exemplo para demonstração.</small>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.map(renderProduct)}
            </div>
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  )
}

export default KitSolar
